// src/components/EventDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, storage, functions } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc, onSnapshot, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCalendarAlt,
    faClock,
    faEdit,
    faTrashAlt,
    faSpinner,
    faExclamationTriangle,
    faRepeat,
    faUserCheck,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import { Alert, Modal, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

import './EventDetailPage.css';

// --- Helper Functions ---
const formatEventDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

const formatEventTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

function EventDetailPage() {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState(null);
    const [error, setError] = useState(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Photo
    const [photoUrl, setPhotoUrl] = useState(null);

    // Recurrence info
    const [recurrenceText, setRecurrenceText] = useState(null);
    const [deleteScope, setDeleteScope] = useState('this'); // 'this', 'future', 'all'

    // RSVP state
    const [myRsvp, setMyRsvp] = useState(false);
    const [rsvpBusy, setRsvpBusy] = useState(false);
    const [rsvps, setRsvps] = useState([]); // admin only

    // Check if eventId looks like a recurring event instance (e.g. seriesId_2026-07-25)
    const isRecurringInstanceId = (id) => /^.+_\d{4}-\d{2}-\d{2}$/.test(id);

    // Fetch event data (with retry for recurring events being regenerated)
    useEffect(() => {
        let cancelled = false;

        const fetchEvent = async () => {
            if (!eventId || !userOrgId) {
                if (!loadingAuth && !userOrgId) {
                    setError("Organization details missing from your account.");
                }
                setLoading(false);
                return;
            }

            const maxRetries = isRecurringInstanceId(eventId) ? 8 : 0;
            const retryDelay = 1000; // 1 second between retries

            for (let attempt = 0; attempt <= maxRetries; attempt++) {
                if (cancelled) return;

                try {
                    const eventDoc = await getDoc(doc(db, 'events', eventId));
                    if (eventDoc.exists()) {
                        const data = eventDoc.data();

                        if (data.orgId !== userOrgId) {
                            setError("You don't have permission to view this event.");
                            setLoading(false);
                            return;
                        }

                        setEvent({ id: eventDoc.id, ...data });
                        setLoading(false);
                        return;
                    }

                    // Document not found — retry if this is a recurring instance
                    if (attempt < maxRetries) {
                        if (attempt === 0) {
                            setLoadingMessage("Updating recurring event...");
                        }
                        await new Promise(resolve => setTimeout(resolve, retryDelay));
                    } else if (maxRetries > 0) {
                        navigate('/events');
                        return;
                    } else {
                        setError("Event not found.");
                    }
                } catch (err) {
                    console.error("Error fetching event:", err);
                    setError("Failed to load event details.");
                    setLoading(false);
                    return;
                }
            }

            setLoading(false);
        };

        if (!loadingAuth) {
            fetchEvent();
        }

        return () => { cancelled = true; };
    }, [eventId, userOrgId, loadingAuth]);

    // Fetch photo URL if event has a photo (prefer 800x800 resized version)
    useEffect(() => {
        const loadPhoto = async () => {
            if (!event || !event.photo) return;
            // Try 800x800 resized version first (resized images are saved as .webp), fall back to original
            const lastDot = event.photo.lastIndexOf('.');
            const resizedPath = lastDot !== -1
                ? `${event.photo.substring(0, lastDot)}_800x800.webp`
                : event.photo;
            try {
                const url = await getDownloadURL(ref(storage, resizedPath));
                setPhotoUrl(url);
            } catch {
                try {
                    const url = await getDownloadURL(ref(storage, event.photo));
                    setPhotoUrl(url);
                } catch (err) {
                    console.warn("Could not load event photo:", err);
                }
            }
        };
        loadPhoto();
    }, [event]);

    // Fetch recurrence info if event is recurring
    useEffect(() => {
        const fetchSeriesInfo = async () => {
            if (!event || !event.seriesId) return;
            try {
                const seriesDoc = await getDoc(doc(db, 'eventSeries', event.seriesId));
                if (seriesDoc.exists()) {
                    const data = seriesDoc.data();
                    const rec = data.recurrence;
                    if (rec) {
                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        const ordinals = ['', 'first', 'second', 'third', 'fourth'];
                        let text = '';
                        switch (rec.frequency) {
                            case 'weekly':
                                text = `Repeats every week on ${dayNames[rec.dayOfWeek]}`;
                                break;
                            case 'biweekly':
                                text = `Repeats every 2 weeks on ${dayNames[rec.dayOfWeek]}`;
                                break;
                            case 'monthly':
                                if (rec.monthlyType === 'dayOfWeek') {
                                    text = `Repeats monthly on the ${ordinals[Math.min(rec.weekOfMonth || 1, 4)]} ${dayNames[rec.dayOfWeek]}`;
                                } else {
                                    const startDate = rec.startDate instanceof Timestamp ? rec.startDate.toDate() : new Date(rec.startDate);
                                    const day = startDate.getDate();
                                    const suffix = (day >= 11 && day <= 13) ? 'th' : ['th','st','nd','rd','th','th','th','th','th','th'][day % 10];
                                    text = `Repeats monthly on the ${day}${suffix}`;
                                }
                                break;
                            case 'yearly': {
                                const startDate = rec.startDate instanceof Timestamp ? rec.startDate.toDate() : new Date(rec.startDate);
                                text = `Repeats yearly on ${startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`;
                                break;
                            }
                            default:
                                text = `Repeats ${rec.frequency}`;
                        }
                        setRecurrenceText(text);
                    }
                }
            } catch (err) {
                console.error("Error fetching series info:", err);
            }
        };
        fetchSeriesInfo();
    }, [event]);

    // Subscribe to the current user's own RSVP doc
    useEffect(() => {
        if (!event || !currentUser) return;
        const unsub = onSnapshot(
            doc(db, 'events', event.id, 'rsvps', currentUser.uid),
            (snap) => setMyRsvp(snap.exists()),
            (err) => console.warn('RSVP subscription error:', err)
        );
        return unsub;
    }, [event, currentUser]);

    // Admins: subscribe to all RSVPs for the count + names
    useEffect(() => {
        if (!event || !isAdmin) return;
        const unsub = onSnapshot(
            collection(db, 'events', event.id, 'rsvps'),
            (snap) => setRsvps(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => console.warn('RSVP list subscription error:', err)
        );
        return unsub;
    }, [event, isAdmin]);

    const eventDate = event?.dateTime instanceof Timestamp
        ? event.dateTime.toDate()
        : (event?.dateTime ? new Date(event.dateTime) : null);
    const isPastEvent = eventDate ? eventDate < new Date() : false;

    const handleToggleRsvp = async () => {
        if (!event || !currentUser || rsvpBusy) return;
        setRsvpBusy(true);
        try {
            const rsvpRef = doc(db, 'events', event.id, 'rsvps', currentUser.uid);
            if (myRsvp) {
                await deleteDoc(rsvpRef);
            } else {
                await setDoc(rsvpRef, {
                    displayName: currentUser.displayName || currentUser.email,
                    orgId: userOrgId,
                    createdAt: serverTimestamp()
                });
            }
        } catch (err) {
            console.error('Error updating RSVP:', err);
            setError('Failed to update RSVP. Please try again.');
        } finally {
            setRsvpBusy(false);
        }
    };

    // Delete handler
    const handleConfirmDelete = async () => {
        if (!event || !isAdmin) return;

        setIsDeleting(true);
        try {
            const deleteEventCallable = httpsCallable(functions, 'deleteEvent');
            const payload = { eventId: event.id };

            if (event.isRecurring && event.seriesId) {
                payload.scope = deleteScope;
            }

            await deleteEventCallable(payload);

            setShowDeleteModal(false);
            navigate('/events', { state: { message: `Event '${event.title}' deleted.` } });
        } catch (err) {
            console.error("Error deleting event:", err);
            setError("Failed to delete event. Please try again.");
            setIsDeleting(false);
        }
    };

    // Loading state
    if (loadingAuth || loading) {
        return (
            <div className="event-detail-page">
                {loadingMessage ? (
                    <div className="event-detail-loading" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#6366f1', marginBottom: '1rem' }} />
                        <p style={{ color: '#64748b', fontWeight: 500 }}>{loadingMessage}</p>
                    </div>
                ) : (
                    <div className="event-detail-loading">
                        <div className="skeleton-header"></div>
                        <div className="skeleton-card"></div>
                    </div>
                )}
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="event-detail-page">
                <div className="container mt-3">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view this event.
                    </Alert>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="event-detail-page">
                <div className="event-detail-header">
                    <div className="container">
                        <div className="event-detail-header-content">
                            <Link to="/events" className="event-back-btn" title="Back to Events">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="event-detail-title">Event Details</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="danger">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {error}
                    </Alert>
                    <Link to="/events" className="btn btn-secondary">
                        Back to Events
                    </Link>
                </div>
            </div>
        );
    }

    // No event found
    if (!event) {
        return (
            <div className="event-detail-page">
                <div className="event-detail-header">
                    <div className="container">
                        <div className="event-detail-header-content">
                            <Link to="/events" className="event-back-btn" title="Back to Events">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="event-detail-title">Event Not Found</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="event-detail-empty">
                        <p>The event you're looking for doesn't exist or has been deleted.</p>
                        <Link to="/events" className="btn btn-primary">
                            Back to Events
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="event-detail-page">
            {/* Page Header */}
            <div className="event-detail-header">
                <div className="container">
                    <div className="event-detail-header-content">
                        <Link to="/events" className="event-back-btn" title="Back to Events">
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Link>
                        <div className="event-detail-header-text">
                            <h1 className="event-detail-title">{event.title}</h1>
                        </div>
                        {isAdmin && (
                            <div className="event-detail-header-actions">
                                <button
                                    className="event-header-action-btn event-header-action-btn-edit"
                                    onClick={() => navigate(`/events/${event.id}/edit`)}
                                    title="Edit Event"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                    <span>Edit</span>
                                </button>
                                <button
                                    className="event-header-action-btn event-header-action-btn-delete"
                                    onClick={() => setShowDeleteModal(true)}
                                    title="Delete Event"
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                    <span>Delete</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="event-detail-content">
                    {/* Event Photo */}
                    {photoUrl && (
                        <div className="event-detail-card event-photo-card">
                            <img src={photoUrl} alt={event.title} className="event-detail-photo" />
                        </div>
                    )}

                    {/* Date & Time Card */}
                    <div className="event-detail-card event-datetime-card">
                        <div className="event-datetime-icon">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                        </div>
                        <div className="event-datetime-info">
                            <div className="event-datetime-date">
                                {formatEventDate(event.dateTime)}
                            </div>
                            <div className="event-datetime-time">
                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                {formatEventTime(event.dateTime)}
                            </div>
                        </div>
                    </div>

                    {/* RSVP */}
                    {!isPastEvent && (
                        <div className="event-detail-card" style={{ padding: '1rem 1.5rem' }}>
                            <button
                                data-testid="rsvp-toggle"
                                onClick={handleToggleRsvp}
                                disabled={rsvpBusy}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                                    padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600,
                                    cursor: rsvpBusy ? 'wait' : 'pointer',
                                    border: myRsvp ? '1px solid #86efac' : '1px solid #cbd5e1',
                                    background: myRsvp ? '#f0fdf4' : '#ffffff',
                                    color: myRsvp ? '#15803d' : '#334155'
                                }}
                            >
                                <FontAwesomeIcon icon={faUserCheck} />
                                {myRsvp ? "You're going" : "I'm going"}
                            </button>
                        </div>
                    )}

                    {/* Admin: who's going */}
                    {isAdmin && (
                        <div className="event-detail-card" data-testid="rsvp-admin-card">
                            <h3 className="event-card-title">
                                <FontAwesomeIcon icon={faUsers} className="me-2" />
                                Going ({rsvps.length})
                            </h3>
                            {rsvps.length === 0 ? (
                                <p className="text-muted mb-0">No RSVPs yet.</p>
                            ) : (
                                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                    {rsvps.map(r => (
                                        <li key={r.id}>{r.displayName}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Recurrence Info */}
                    {recurrenceText && (
                        <div className="event-detail-card" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '1rem 1.5rem',
                            background: '#eff6ff',
                            borderColor: '#bfdbfe'
                        }}>
                            <FontAwesomeIcon icon={faRepeat} style={{ color: '#3b82f6', fontSize: '1.1rem' }} />
                            <span style={{ color: '#1e40af', fontWeight: 500 }}>{recurrenceText}</span>
                        </div>
                    )}

                    {/* Description Card */}
                    {event.description && (
                        <div className="event-detail-card">
                            <h3 className="event-card-title">Description</h3>
                            <div className="event-description-content">
                                {event.description.split('\n').map((paragraph, index) => (
                                    <p key={index}>{paragraph}</p>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No description placeholder */}
                    {!event.description && (
                        <div className="event-detail-card event-no-description">
                            <p>No description provided for this event.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => !isDeleting && setShowDeleteModal(false)} centered className="events-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Event</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete <strong>"{event.title}"</strong>?</p>
                    {event.isRecurring && event.seriesId && (
                        <div style={{ marginBottom: '1rem' }}>
                            <p style={{ fontWeight: 500, marginBottom: '0.5rem' }}>This is a recurring event. What would you like to delete?</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {[
                                    { value: 'this', label: 'This event only', desc: 'Remove just this occurrence' },
                                    { value: 'future', label: 'This and all future events', desc: 'Keep past occurrences' },
                                    { value: 'all', label: 'All events in series', desc: 'Delete the entire series' },
                                ].map(opt => (
                                    <label key={opt.value} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                        padding: '0.5rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                                        background: deleteScope === opt.value ? '#fef2f2' : '#f8fafc',
                                        border: `1px solid ${deleteScope === opt.value ? '#fecaca' : '#e2e8f0'}`
                                    }}>
                                        <input
                                            type="radio" name="deleteScope"
                                            value={opt.value}
                                            checked={deleteScope === opt.value}
                                            onChange={(e) => setDeleteScope(e.target.value)}
                                            disabled={isDeleting}
                                            style={{ marginTop: '0.25rem' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{opt.label}</div>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{opt.desc}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="text-muted mb-0">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete Event"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default EventDetailPage;
